import React from 'react';
import { fmtDate } from '../../utils/formatters';
import { BoletoItem, boletoCapabilities } from './boletoCapabilities';
import { TONE_TEXT } from '../../utils/statusColors';
import CascaDeModal from '../shared/CascaDeModal';
import { campo } from '../../design';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const nomeCliente = (b: BoletoItem) => b.clientDisplayName || b.clientTradeName || b.clientName;

/**
 * Moldura dos diálogos de boleto = a CascaDeModal (Fase 3). Não tinha ×: o × da
 * casca chama o mesmo Cancelar/Fechar e NÃO age enquanto `bloqueado` (registro ou
 * baixa rodando no Sicredi), igual ao Cancelar desabilitado. Não fecha ao clicar fora.
 */
const Shell: React.FC<{
  title: string; subtitle: React.ReactNode; wide?: boolean; children: React.ReactNode; footer: React.ReactNode;
  onClose: () => void; bloqueado?: boolean;
}> = ({ title, subtitle, wide, children, footer, onClose, bloqueado }) => (
  <CascaDeModal
    largura={wide ? 'sm' : 'xs'}
    titulo={title}
    onClose={() => { if (!bloqueado) onClose(); }}
    topo={<p className="px-5 pt-3 text-sm text-slate-400">{subtitle}</p>}
    corpoClassName="p-5 text-sm"
    rodape={<div className="ml-auto flex gap-3">{footer}</div>}
  >
    {children}
  </CascaDeModal>
);

const Linha: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between"><span className="text-slate-400">{label}</span>{children}</div>
);

const Detalhes: React.FC<{ b: BoletoItem; comNossoNumero?: boolean }> = ({ b, comNossoNumero }) => (
  <div className="space-y-2">
    <Linha label="Fatura"><span className="text-white font-mono">{b.code}</span></Linha>
    <Linha label="Cliente"><span className="text-white text-right max-w-[260px] truncate">{nomeCliente(b)}</span></Linha>
    <Linha label="Valor"><span className="text-white font-mono">R$ {fmt(b.totalValue)}</span></Linha>
    {comNossoNumero && <Linha label="Nosso Número"><span className="text-white font-mono">{b.nossoNumero}</span></Linha>}
    <Linha label="Vencimento"><span className="text-white">{fmtDate(b.dueDate)}</span></Linha>
  </div>
);

const btnCancelar = 'px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors disabled:opacity-50';
const btnPrimario = 'px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50';

// ── Emissão híbrida individual ─────────────────────────────────────────────
export const ModalEmitirHibrido: React.FC<{
  target: BoletoItem; running: boolean; onCancel: () => void; onConfirm: () => void;
}> = ({ target, running, onCancel, onConfirm }) => (
  <Shell
    onClose={onCancel}
    bloqueado={running}
    title="Emitir boleto PIX/híbrido via API"
    subtitle={<>Registra na hora no Sicredi (sem remessa CNAB): boleto com <span className="text-white font-medium">QR PIX</span> + linha digitável. Multa 2% e juros 0,033%/dia, como no CNAB.</>}
    footer={
      <>
        <button onClick={onCancel} disabled={running} className={btnCancelar}>Cancelar</button>
        <button onClick={onConfirm} disabled={running} className={`${btnPrimario} bg-primary hover:bg-primary-strong`}>
          {running ? 'Registrando no Sicredi...' : 'Emitir híbrido'}
        </button>
      </>
    }
  >
    <Detalhes b={target} />
  </Shell>
);

// ── Baixa via API ──────────────────────────────────────────────────────────
export const ModalBaixaApi: React.FC<{
  target: BoletoItem; running: boolean; onCancel: () => void; onConfirm: () => void;
}> = ({ target, running, onCancel, onConfirm }) => (
  <Shell
    onClose={onCancel}
    bloqueado={running}
    title="Baixar boleto via API Sicredi"
    subtitle={<>A baixa é <span className="text-red-400 font-medium">imediata e definitiva</span> no banco — o cliente não conseguirá mais pagar este boleto.</>}
    footer={
      <>
        <button onClick={onCancel} disabled={running} className={btnCancelar}>Cancelar</button>
        <button onClick={onConfirm} disabled={running} className={`${btnPrimario} bg-red-600 hover:bg-red-500`}>
          {running ? 'Baixando no Sicredi...' : 'Confirmar baixa'}
        </button>
      </>
    }
  >
    <Detalhes b={target} comNossoNumero />
  </Shell>
);

// ── Baixa via API em LOTE ──────────────────────────────────────────────────
// Substituiu a remessa CNAB de baixa, que só gerava um arquivo para subir no
// portal. Agora o clique tem efeito imediato no banco, então passa a exigir
// confirmação como a baixa individual.
export const ModalBaixaLote: React.FC<{
  alvos: BoletoItem[]; running: boolean; onCancel: () => void; onConfirm: () => void;
}> = ({ alvos, running, onCancel, onConfirm }) => {
  const total = alvos.reduce((s, b) => s + (Number(b.totalValue) || 0), 0);
  return (
    <Shell
      onClose={onCancel}
      bloqueado={running}
      title={`Baixar ${alvos.length} boleto(s) via API Sicredi`}
      subtitle={<>A baixa é <span className="text-red-400 font-medium">imediata e definitiva</span> no banco — os clientes não conseguirão mais pagar estes boletos.</>}
      wide
      footer={
        <>
          <button onClick={onCancel} disabled={running} className={btnCancelar}>Cancelar</button>
          <button onClick={onConfirm} disabled={running} className={`${btnPrimario} bg-red-600 hover:bg-red-500`}>
            {running ? 'Baixando no Sicredi...' : `Confirmar baixa (${alvos.length})`}
          </button>
        </>
      }
    >
      <div className="space-y-2 mb-4">
        <Linha label="Títulos"><span className="text-white font-mono">{alvos.length}</span></Linha>
        <Linha label="Valor somado"><span className="text-white font-mono">R$ {fmt(total)}</span></Linha>
      </div>
      <div className="border-t border-slate-700 pt-3 space-y-1 max-h-52 overflow-y-auto">
        {alvos.map(b => (
          <div key={b.id} className="flex justify-between text-xs">
            <span className="text-slate-300 font-mono">{b.code}</span>
            <span className="text-slate-400 truncate px-2 flex-1 text-right">{nomeCliente(b)}</span>
            <span className="text-slate-300 font-mono">{b.nossoNumero}</span>
          </div>
        ))}
      </div>
    </Shell>
  );
};

// ── Alterar vencimento ─────────────────────────────────────────────────────
// E1 (12/10/2026, aprovado pelo Heitor, opção a): com título vivo no banco o Salvar TRAVA. Antes avisava e salvava: a
// fatura ganhava a data nova e o boleto com o cliente seguia com a velha. O caminho é baixar, alterar e emitir de novo;
// o modal diz isso e, quando a baixa é possível, leva direto a ela. O gesto único (baixar e reemitir num clique) fica
// para depois.
export const ModalVencimento: React.FC<{
  target: BoletoItem; value: string; saving: boolean;
  onChange: (v: string) => void; onCancel: () => void; onSave: () => void;
  /** Abre a baixa deste título (só quando a baixa pela API é possível e o perfil pode). */
  onBaixarPrimeiro?: () => void;
}> = ({ target, value, saving, onChange, onCancel, onSave, onBaixarPrimeiro }) => {
  const vivo = boletoCapabilities(target).temTituloVivo;
  return (
    <Shell
      onClose={onCancel}
      bloqueado={saving}
      title="Alterar Vencimento"
      subtitle={`Fatura ${target.code} - ${nomeCliente(target)}`}
      footer={
        <>
          <button onClick={onCancel} className={btnCancelar}>Cancelar</button>
          {vivo && onBaixarPrimeiro && (
            // Neutro: este botão só ABRE a baixa; o vermelho é do "Confirmar baixa" do modal seguinte, que é o que não volta.
            <button onClick={onBaixarPrimeiro} className={btnCancelar}>
              Baixar este título primeiro
            </button>
          )}
          <button onClick={onSave} disabled={vivo || !value || saving} className={`${btnPrimario} bg-primary hover:bg-primary-strong disabled:bg-slate-700 disabled:text-slate-500`}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <label className="block text-slate-400 text-sm mb-1">Nova data de vencimento</label>
      <input
        type="date"
        value={value}
        disabled={vivo}
        onChange={e => onChange(e.target.value)}
        className={`w-full px-3 py-2 rounded-lg focus:outline-none disabled:opacity-50 ${campo}`}
      />
      {vivo && (
        <p role="alert" className={`text-xs mt-3 ${TONE_TEXT.warning}`} data-titulo-vivo>
          ⚠ O boleto {target.nossoNumero} está vivo no Sicredi ({target.boletoStatus}). Mudar a data só na fatura deixaria
          o cliente com o boleto no vencimento antigo. Para mudar: baixe o título (⋯ → Baixar (cancelar) no Sicredi),
          altere o vencimento aqui e emita o boleto de novo.
        </p>
      )}
    </Shell>
  );
};

// ── Emissão em lote ────────────────────────────────────────────────────────
export interface LoteState {
  month: string;
  preview: any | null;
  running: boolean;
  job: any | null;
}

export const ModalLote: React.FC<{
  state: LoteState;
  onChangeMonth: (m: string) => void;
  onClose: () => void;
  onRun: () => void;
}> = ({ state, onChangeMonth, onClose, onRun }) => (
  <Shell
    onClose={onClose}
    bloqueado={state.running}
    wide
    title={`⚡ Emitir boletos híbridos em lote${state.month ? ` — ${state.month}` : ''}`}
    subtitle={<>Registra via API do Sicredi (QR PIX) todas as faturas de boleto do mês <span className="text-white">sem boleto ativo</span>. <span className="text-white font-medium">Não envia e-mail</span> — o envio continua manual.</>}
    footer={
      <>
        <button onClick={onClose} disabled={state.running} className={btnCancelar}>
          {state.job ? 'Fechar' : 'Cancelar'}
        </button>
        {!state.job && (
          <button
            onClick={onRun}
            disabled={state.running || !/^\d{4}-\d{2}$/.test(state.month) || !state.preview || state.preview.total === 0}
            className={`${btnPrimario} bg-primary hover:bg-primary-strong`}
          >
            {state.running ? 'Iniciando…' : `Emitir ${state.preview?.total ?? ''} híbrido(s)`}
          </button>
        )}
      </>
    }
  >
    {/* Competência é escolhida à mão: emitir registra título real no banco,
        então o mês nunca é inferido do filtro da tela. */}
    <div className="mb-4">
      <label className="block text-xs text-slate-400 mb-1">Competência a emitir</label>
      <input
        type="month"
        value={state.month}
        disabled={state.running || !!state.job}
        onChange={e => onChangeMonth(e.target.value)}
        className={`px-3 py-2 ${campo} rounded-lg text-sm disabled:opacity-50`}
      />
      {!state.month && <p className="text-xs text-amber-400 mt-1">Escolha o mês de competência para listar as faturas.</p>}
    </div>

    {state.job && (
      <div className="mb-3">
        <div className="flex justify-between text-xs text-slate-400 mb-1">
          <span>{state.job.status === 'running' ? 'Emitindo no Sicredi…' : 'Concluído'}</span>
          <span>{state.job.processed}/{state.job.total}</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${state.job.total ? Math.round((state.job.processed / state.job.total) * 100) : 0}%` }}
          />
        </div>
      </div>
    )}

    {state.month && !state.preview && !state.job && <div className="text-slate-400">Carregando candidatas…</div>}

    {state.preview && !state.job && (
      state.preview.total === 0 ? (
        <div className="text-slate-400">Nenhuma fatura candidata neste mês (todas já têm boleto ativo ou estão pagas/canceladas).</div>
      ) : (
        <>
          <div className="text-white font-medium mb-2">{state.preview.total} fatura(s) serão registradas:</div>
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {state.preview.candidatas.map((c: any) => (
              <div key={c.invoiceId} className="flex justify-between gap-2 text-xs bg-slate-700/40 rounded px-2 py-1.5">
                <span className="font-mono text-slate-300">{c.code}</span>
                <span className="text-slate-400 truncate flex-1">{c.client}</span>
                <span className="font-mono text-white">R$ {fmt(c.totalValue)}</span>
                <span className="text-slate-400">{c.dueDate ? c.dueDate.split('-').reverse().join('/') : '—'}</span>
              </div>
            ))}
          </div>
        </>
      )
    )}

    {state.job && (
      <div className="space-y-3">
        <div className={`${TONE_TEXT.success} font-medium`}>✓ {(state.job.ok || []).length} boleto(s) híbrido(s) registrado(s)</div>
        {(state.job.erros || []).length > 0 && (
          <div>
            <div className="text-red-400 font-medium mb-1">✕ {state.job.erros.length} erro(s):</div>
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {state.job.erros.map((e: any) => (
                <div key={e.invoiceId} className="text-xs bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5">
                  <span className="font-mono text-slate-300">{e.code}</span> <span className="text-red-300">{e.erro}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {state.job.status === 'interrupted' && (
          <div className="text-xs text-amber-400">⚠ O processo foi reiniciado durante a execução. Reabra o lote para emitir o que faltou (emissão é idempotente, não duplica).</div>
        )}
        {state.job.status !== 'running' && (
          <p className="text-xs text-slate-400">Os PDFs/e-mails já saem com QR PIX + boleto oficial do banco quando você enviar.</p>
        )}
      </div>
    )}
  </Shell>
);

// ── Conciliação sob demanda ────────────────────────────────────────────────
export const ModalConciliacao: React.FC<{
  dia: string; running: boolean; result: any | null;
  onChangeDia: (d: string) => void; onClose: () => void; onRun: (dryRun: boolean) => void;
}> = ({ dia, running, result, onChangeDia, onClose, onRun }) => (
  <Shell
    onClose={onClose}
    bloqueado={running}
    wide
    title="Conciliar com o Sicredi"
    subtitle="Consulta os títulos liquidados no dia direto no banco (fonte autoritativa) e dá baixa no que o webhook não trouxe. Roda sozinha às 06h, 12h e 19h."
    footer={
      <>
        <button onClick={onClose} disabled={running} className={btnCancelar}>Fechar</button>
        <button onClick={() => onRun(true)} disabled={running || !dia} className={`${btnPrimario} bg-slate-600 hover:bg-slate-500`}>
          {running ? 'Consultando…' : 'Simular (não dá baixa)'}
        </button>
        <button onClick={() => onRun(false)} disabled={running || !dia} className={`${btnPrimario} bg-primary hover:bg-primary-strong`}>
          Conciliar de verdade
        </button>
      </>
    }
  >
    <label className="block text-xs text-slate-400 mb-1">Dia a conciliar</label>
    <input
      type="date"
      value={dia}
      disabled={running}
      onChange={e => onChangeDia(e.target.value)}
      className={`px-3 py-2 ${campo} rounded-lg text-sm disabled:opacity-50`}
    />
    {result && (
      <div className="mt-4 space-y-2">
        <div className="text-white font-medium">
          {result.dryRun ? 'Simulação' : 'Conciliação'} de {result.dia}: {result.totalNoBanco ?? 0} liquidado(s) no banco
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span className={TONE_TEXT.success}>{result.aplicados ?? 0} baixa(s) {result.dryRun ? 'a aplicar' : 'aplicada(s)'}</span>
          <span className="text-slate-400">{result.jaLiquidados ?? 0} já liquidada(s)</span>
          <span className={result.naoEncontrados ? 'text-amber-400' : 'text-slate-400'}>
            {result.naoEncontrados ?? 0} sem fatura correspondente
          </span>
        </div>
        {(result.itens || []).length > 0 && (
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {result.itens.map((i: any, idx: number) => (
              <div key={idx} className="flex justify-between gap-2 text-xs bg-slate-700/40 rounded px-2 py-1.5">
                <span className="font-mono text-slate-300">{i.nossoNumero}</span>
                <span className={`truncate flex-1 ${i.aplicado ? TONE_TEXT.success : 'text-slate-400'}`}>{i.resultado}</span>
                <span className="font-mono text-white">R$ {fmt(i.valor)}</span>
              </div>
            ))}
          </div>
        )}
        {(result.alertas || []).length > 0 && (
          <div className="text-xs text-amber-300 space-y-1">
            {result.alertas.map((e: any, i: number) => <div key={i}>⚠ {typeof e === 'string' ? e : JSON.stringify(e)}</div>)}
          </div>
        )}
      </div>
    )}
  </Shell>
);
